import { MAX_CAREER_SAVE_CHARS } from "@golazo/shared";
import type { HallOfFameEntry } from "./career.js";
import { CAREER_VERSION, type Career, type SeasonSummary } from "./model.js";
import type { KitUnlocks } from "./unlocks.js";
import { WORLD_VERSION, createWorld } from "./world.js";

/**
 * La carrera en la cuenta: el formato que viaja y la regla de quien gana.
 *
 * Puro, como el resto de `career/`: aqui no se manda ni se guarda nada. Lo
 * usa `game/careerCloud.ts`, que es quien habla con el servidor.
 *
 * ## Por que se compacta
 *
 * Una carrera entera (17 a 35 años) pesa ~32 KB y el servidor corta cualquier
 * mensaje por encima de 16 KB, que es una defensa contra abusos y no se toca
 * por esto. Dos cosas se llevan el 75 %:
 *
 *  - los 32 clubes con sus nombres, chistes y estadios (~9 KB). Todo eso sale
 *    de la semilla (`createWorld`); lo unico que cambia con los años es la
 *    division y la fuerza de cada uno. Se sube eso y se regenera el resto.
 *  - el movil (~15 KB). Viajan los ultimos mensajes, no los 80.
 *
 * ## Quien gana al juntar dos copias
 *
 *  - Equipaciones y salon de la fama: se SUMAN. Nunca se pierde nada ganado.
 *  - La carrera, si es la misma (misma semilla): la mas avanzada. Una copia
 *    vieja no puede pisar partidos ya jugados.
 *  - Si son carreras distintas: la ultima que se toco. Es la que el jugador
 *    tiene en la cabeza (empezo otra, o se retiro y empezo otra).
 */

export const CLOUD_VERSION = 1;
/** Mensajes del movil que viajan. Los de antes se quedan solo en el aparato que los vio. */
export const FEED_SYNC = 24;
/** Bustos del salon que viajan como mucho (los mas nuevos). */
export const HALL_SYNC = 10;

type PackedClub = [id: string, tier: number, rating: number];

/** Una temporada del historial en una fila, sin los nombres de campo (el nombre del club sale del id). */
type PackedSummary = [
  number: number,
  age: number,
  tier: number,
  club: string,
  position: number,
  points: number,
  played: number,
  goals: number,
  assists: number,
  mvps: number,
  grade: number,
  champion: 0 | 1,
  promoted: 0 | 1,
  relegated: 0 | 1,
];

interface PackedCareer extends Omit<Career, "clubs" | "history" | "titles"> {
  /** Solo lo que cambia con los años; el resto sale de `createWorld(seed)`. */
  clubs: PackedClub[];
  /** Los titulos no viajan: son las temporadas del historial en las que fuiste campeon. */
  history: PackedSummary[];
  /** Version de las listas de nombres: si no es la de este juego, no se puede regenerar. */
  world: number;
}

/** Un busto del salon con los titulos en filas cortas: [division, temporada, club]. */
type PackedEntry = Omit<HallOfFameEntry, "titles"> & { titles: Array<[number, number, string]> };

export interface CloudSave {
  v: number;
  /** Cuando se toco la carrera por ultima vez (reloj del aparato que la subio). */
  at: number;
  unlocks: KitUnlocks;
  hall: PackedEntry[];
  career: PackedCareer | null;
}

export interface SaveState {
  career: Career | null;
  /** Ultima vez que se guardo la carrera (0 si nunca). */
  at: number;
  unlocks: KitUnlocks;
  hall: HallOfFameEntry[];
}

export function packCareer(c: Career, feed = FEED_SYNC): PackedCareer {
  const { clubs, history, titles: _titles, ...rest } = c;
  const bit = (b: boolean): 0 | 1 => (b ? 1 : 0);
  return {
    ...rest,
    feed: feed > 0 ? c.feed.slice(-feed) : [],
    clubs: clubs.map((club) => [club.id, club.tier, club.rating]),
    history: history.map((h) => [
      h.number,
      h.age,
      h.tier,
      h.club,
      h.position,
      h.points,
      h.played,
      h.goals,
      h.assists,
      h.mvps,
      h.grade,
      bit(h.champion),
      bit(h.promoted),
      bit(h.relegated),
    ]),
    world: WORLD_VERSION,
  };
}

function unpackSummary(row: unknown, nameOf: (id: string) => string | undefined): SeasonSummary | null {
  if (!Array.isArray(row) || row.length !== 14 || typeof row[3] !== "string") return null;
  const [number, age, tier, club, position, points, played, goals, assists, mvps, grade, champion, promoted, relegated] =
    row as PackedSummary;
  const clubName = nameOf(club);
  if (clubName === undefined) return null;
  return {
    number,
    age,
    tier,
    club,
    clubName,
    position,
    points,
    played,
    goals,
    assists,
    mvps,
    grade,
    champion: champion === 1,
    promoted: promoted === 1,
    relegated: relegated === 1,
  };
}

function packEntry(e: HallOfFameEntry): PackedEntry {
  return { ...e, titles: e.titles.map((x) => [x.tier, x.season, x.clubName]) };
}

function unpackEntry(raw: unknown): HallOfFameEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Partial<PackedEntry>;
  if (typeof e.id !== "string" || typeof e.name !== "string" || !Array.isArray(e.titles) || !Array.isArray(e.clubs)) {
    return null;
  }
  // Filas cortas; y tambien objetos enteros, que es como viajaban antes de
  // compactarlos (una copia subida entonces no puede perder sus titulos).
  const titles = (e.titles as unknown[]).flatMap((x) => {
    if (Array.isArray(x) && x.length === 3) return [{ tier: Number(x[0]), season: Number(x[1]), clubName: String(x[2]) }];
    if (x && typeof x === "object" && "clubName" in x) {
      const t = x as { tier: number; season: number; clubName: string };
      return [{ tier: t.tier, season: t.season, clubName: t.clubName }];
    }
    return [];
  });
  return { ...(e as PackedEntry), titles };
}

/** Del formato de viaje a una carrera entera. `null` si no cuadra (otra version, datos rotos). */
export function unpackCareer(p: unknown): Career | null {
  if (!p || typeof p !== "object") return null;
  const packed = p as Partial<PackedCareer>;
  if (packed.version !== CAREER_VERSION || packed.world !== WORLD_VERSION) return null;
  if (typeof packed.seed !== "number" || !Array.isArray(packed.clubs) || !packed.season || !packed.player) return null;
  const world = createWorld(packed.seed);
  if (packed.clubs.length !== world.length) return null;
  const byId = new Map(world.map((club) => [club.id, club]));
  for (const entry of packed.clubs) {
    if (!Array.isArray(entry) || entry.length !== 3) return null;
    const [id, tier, rating] = entry;
    const club = typeof id === "string" ? byId.get(id) : undefined;
    if (!club || typeof tier !== "number" || typeof rating !== "number") return null;
    club.tier = tier;
    club.rating = rating;
  }
  if (!Array.isArray(packed.history) || !Array.isArray(packed.feed)) return null;
  const nameOf = (id: string): string | undefined => byId.get(id)?.name;
  const history: SeasonSummary[] = [];
  for (const row of packed.history) {
    const summary = unpackSummary(row, nameOf);
    if (!summary) return null;
    history.push(summary);
  }
  // Los titulos son exactamente las temporadas ganadas (ver `endSeason`).
  const titles = history
    .filter((h) => h.champion)
    .map((h) => ({ season: h.number, tier: h.tier, club: h.club, clubName: h.clubName }));
  const { world: _world, clubs: _clubs, history: _history, ...rest } = packed as PackedCareer;
  return { ...rest, clubs: world, history, titles } as Career;
}

/**
 * Lo que se sube, ya en texto y por debajo del tope. Si no cabe, se van
 * quitando primero bustos viejos del salon y despues mensajes del movil: la
 * carrera en si (partidos, tabla, historial) viaja siempre entera.
 */
export function packSave(state: SaveState, max = MAX_CAREER_SAVE_CHARS): string | null {
  const hallSorted = [...state.hall].sort((a, b) => b.retiredAt - a.retiredAt);
  const attempts: Array<{ hall: number; feed: number }> = [];
  for (let hall = Math.min(HALL_SYNC, hallSorted.length); hall >= 0; hall--) attempts.push({ hall, feed: FEED_SYNC });
  for (const feed of [12, 4, 0]) attempts.push({ hall: 0, feed });
  for (const a of attempts) {
    const save: CloudSave = {
      v: CLOUD_VERSION,
      at: state.at,
      unlocks: state.unlocks,
      hall: hallSorted.slice(0, a.hall).map(packEntry),
      career: state.career ? packCareer(state.career, a.feed) : null,
    };
    const text = JSON.stringify(save);
    if (text.length <= max) return text;
  }
  return null;
}

/** Lo que baja, ya desempaquetado. `null` si no se puede leer. */
export function parseSave(raw: string | null | undefined): SaveState | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const s = parsed as Partial<CloudSave>;
  if (s.v !== CLOUD_VERSION || typeof s.at !== "number") return null;
  const unlocks =
    s.unlocks && Array.isArray(s.unlocks.kits)
      ? { kits: s.unlocks.kits.filter((k): k is string => typeof k === "string"), custom: s.unlocks.custom === true }
      : { kits: [], custom: false };
  const hall = Array.isArray(s.hall)
    ? s.hall.map(unpackEntry).filter((e): e is HallOfFameEntry => e !== null)
    : [];
  return { career: s.career ? unpackCareer(s.career) : null, at: s.at, unlocks, hall };
}

/** Cuanto ha avanzado una carrera. Solo tiene sentido entre copias de la MISMA carrera. */
export function progressOf(c: Career): number {
  const phase = c.phase === "retired" ? 2 : c.phase === "offseason" ? 1 : 0;
  // Temporada, luego fase, luego jornada. Un partido apuntado "sin ti" avanza
  // la jornada igual que uno jugado: los dos son el resultado que cuenta.
  return c.season.number * 1000 + phase * 100 + c.season.matchday;
}

export type CareerPick = "local" | "remote";

export function pickCareer(local: SaveState, remote: SaveState): CareerPick {
  if (!remote.career) return "local";
  if (!local.career) return "remote";
  if (local.career.seed === remote.career.seed) {
    return progressOf(remote.career) > progressOf(local.career) ? "remote" : "local";
  }
  return remote.at > local.at ? "remote" : "local";
}

/** Une dos salones: todos los bustos, sin repetir, del mas nuevo al mas viejo. */
export function mergeHall(a: readonly HallOfFameEntry[], b: readonly HallOfFameEntry[]): HallOfFameEntry[] {
  const byId = new Map<string, HallOfFameEntry>();
  for (const e of [...a, ...b]) if (!byId.has(e.id)) byId.set(e.id, e);
  return [...byId.values()].sort((x, y) => y.retiredAt - x.retiredAt);
}

/** ¿Tiene `mine` algo que `theirs` no? Entonces hay que subir. */
export function hasMore(mine: SaveState, theirs: SaveState | null, pick: CareerPick): boolean {
  if (!theirs) return mine.career !== null || mine.hall.length > 0 || mine.unlocks.kits.length > 2 || mine.unlocks.custom;
  if (mine.unlocks.kits.some((k) => !theirs.unlocks.kits.includes(k))) return true;
  if (mine.unlocks.custom && !theirs.unlocks.custom) return true;
  const theirHall = new Set(theirs.hall.map((e) => e.id));
  if (mine.hall.some((e) => !theirHall.has(e.id))) return true;
  if (pick === "local" && mine.career) {
    const t = theirs.career;
    if (!t || t.seed !== mine.career.seed || progressOf(t) !== progressOf(mine.career)) return true;
    if (t.nextMessageId !== mine.career.nextMessageId || t.phase !== mine.career.phase) return true;
  }
  return false;
}
